import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Field, Icon, Input, Lookup, Modal, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import {
  useApi,
  type CompanyView,
  type LoginEventView,
  type PermissionRow,
  type NumberSeriesView,
  type UserView,
  type WorkflowView,
  type ChangelogEntry,
} from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths } from '../app/router.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { EmailTemplates, Outbox, PrintTemplates } from './TemplatePages.js'
import { IntegrationPage } from './IntegrationPage.js'
import { Branding, DataExports, DataImports } from './DataPages.js'
import { formatDate, formatDateTime } from '../framework/format.js'

/**
 * Administration (docs/11 §User and Permission, §Setup).
 *
 * Five screens behind one hub: who is on the account, what each role may do,
 * what documents are called, who approves them, and the companies themselves.
 */

const SECTIONS = [
  { key: 'users', label: 'People', blurb: 'Who is on this account, and what each of them may do.' },
  { key: 'roles', label: 'Roles and permissions', blurb: 'What each role may do with each kind of document.' },
  { key: 'register', label: 'Sign-in register', blurb: 'Every attempt to sign in, successful or not.' },
  { key: 'numbering', label: 'Document numbering', blurb: 'The prefix and the next number for every document type.' },
  { key: 'workflows', label: 'Approval routes', blurb: 'The steps a document goes through before it is approved.' },
  { key: 'companies', label: 'Companies', blurb: 'The legal entities this account keeps books for.' },
  { key: 'templates', label: 'Print templates', blurb: 'The layout each kind of document prints in.' },
  { key: 'email', label: 'Email templates', blurb: 'The wording of every message this system sends out.' },
  { key: 'outbox', label: 'Email outbox', blurb: 'What has been sent, what is waiting, and what bounced.' },
  { key: 'integration', label: 'Accounting integration', blurb: 'Which accounting system the books go to, and what is still waiting.' },
  { key: 'imports', label: 'Load data in bulk', blurb: 'Masters and revised GST rates, from a spreadsheet: download, edit, check, import.' },
  { key: 'exports', label: 'Export data', blurb: 'Every document and master in this company, in one archive.' },
  { key: 'branding', label: 'Branding', blurb: 'The logo on the sign-in page and on printed documents.' },
  { key: 'changelog', label: 'Changelog', blurb: 'Every administrative change: who made it, what it was, and when.' },
] as const

export const AdminHub = ({ companyId }: { companyId: string }) => (
  <div className="page">
    <h1 className="page-title">Administration</h1>
    <p className="page-subtitle">The settings that decide who may do what, and what things are called.</p>

    <Panel title="Sections">
      <div className="tile-grid">
        {SECTIONS.map((section) => (
          <div className="tile" key={section.key}>
            <div className="tile__body">
              <Link to={paths.admin(companyId, section.key)} className="tile__title">{section.label}</Link>
              <p className="tile__desc">{section.blurb}</p>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  </div>
)

export const AdminPage = ({ companyId, section }: { companyId: string; section: string }) => {
  const known = SECTIONS.find((s) => s.key === section)
  if (!known) {
    return (
      <div className="page">
        <h1 className="page-title">Page not found</h1>
        <p className="page-subtitle">There is no administration page at “{section}”.</p>
      </div>
    )
  }

  return (
    <div className="page">
      <Link to={paths.admin(companyId, '')} className="crumb"><Icon name="left" size={15} /> Administration</Link>
      <h1 className="page-title">{known.label}</h1>
      <p className="page-subtitle">{known.blurb}</p>

      {section === 'users' ? <People /> : null}
      {section === 'roles' ? <Roles /> : null}
      {section === 'register' ? <LoginRegister /> : null}
      {section === 'numbering' ? <Numbering /> : null}
      {section === 'workflows' ? <Workflows /> : null}
      {section === 'companies' ? <Companies companyId={companyId} /> : null}
      {section === 'templates' ? <PrintTemplates /> : null}
      {section === 'email' ? <EmailTemplates /> : null}
      {section === 'outbox' ? <Outbox /> : null}
      {section === 'integration' ? <IntegrationPage companyId={companyId} /> : null}
      {section === 'imports' ? <DataImports /> : null}
      {section === 'exports' ? <DataExports /> : null}
      {section === 'branding' ? <Branding /> : null}
      {section === 'changelog' ? <Changelog /> : null}
    </div>
  )
}

/* ---- people -------------------------------------------------------------- */

const ROLE_OPTIONS = [
  { value: 'OWNER', label: 'Owner' },
  { value: 'ADMIN', label: 'Administrator' },
  { value: 'MEMBER', label: 'Member' },
]

const EMPTY_PERSON = { name: '', email: '', role: 'MEMBER', designation: '', mobile: '', roleId: '', employeeId: '', employeeLabel: '' }

const People = () => {
  const api = useApi()
  const client = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState(EMPTY_PERSON)
  const [issued, setIssued] = useState<{ name: string; password: string } | null>(null)
  const [accessFor, setAccessFor] = useState<UserView | null>(null)
  const [linkFor, setLinkFor] = useState<UserView | null>(null)
  const [linkTo, setLinkTo] = useState<{ id: string; label: string }>({ id: '', label: '' })
  const [error, setError] = useState<string | null>(null)

  const users = useQuery({ queryKey: ['adminUsers', api.companyId], queryFn: () => api.adminUsers() })
  // The roles the business named for itself: Purchase Manager, Site Engineer…
  const businessRoles = useQuery({
    queryKey: ['masterList', api.companyId, 'app_role'],
    queryFn: () => api.masterList('app_role', { pageSize: '200' }),
    staleTime: 60_000,
  })
  const roleOptions = [
    { value: '', label: 'None yet' },
    ...(businessRoles.data?.rows ?? []).map((r) => ({ value: String(r['id']), label: String(r['name'] ?? r['code'] ?? '') })),
  ]
  const refresh = (next: readonly UserView[]): void => {
    client.setQueryData(['adminUsers', api.companyId], next)
    setError(null)
  }

  const add = useMutation({
    mutationFn: () => api.adminCreateUser(draft.name, draft.email, draft.role, {
      designation: draft.designation.trim() || null,
      mobile: draft.mobile.trim() || null,
      roleId: draft.roleId || null,
      employeeId: draft.employeeId || null,
    }),
    onSuccess: (result) => {
      setAdding(false)
      setDraft(EMPTY_PERSON)
      setIssued({ name: result.user.name, password: result.temporaryPassword })
      void client.invalidateQueries({ queryKey: ['adminUsers', api.companyId] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => api.adminSetRole(id, role),
    onSuccess: refresh,
    onError: (err) => setError(messageFor(err)),
  })

  const link = useMutation({
    mutationFn: ({ id, employeeId }: { id: string; employeeId: string | null }) => api.adminSetUserEmployee(id, employeeId),
    onSuccess: (next) => {
      refresh(next)
      setLinkFor(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.adminSetActive(id, active),
    onSuccess: refresh,
    onError: (err) => setError(messageFor(err)),
  })

  const reset = useMutation({
    mutationFn: (user: UserView) => api.adminResetPassword(user.id).then((r) => ({ user, ...r })),
    onSuccess: (result) => {
      setIssued({ name: result.user.name, password: result.temporaryPassword })
      void client.invalidateQueries({ queryKey: ['adminUsers', api.companyId] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (users.isLoading) return <Loading />
  if (users.error) return <ErrorBlock error={users.error} />

  const columns: ColumnDef<UserView>[] = [
    { key: 'name', header: 'Name',
      render: (row) => (
        <>
          <div>{row.name}</div>
          <div className="cell-sub">{row.email}</div>
        </>
      ) },
    { key: 'role', header: 'Role', width: 190,
      render: (row) => (
        <Select
          value={row.role}
          onChange={(role) => setRole.mutate({ id: row.id, role })}
          options={ROLE_OPTIONS}
          aria-label={`Role for ${row.name}`}
        />
      ) },
    /*
      Who they are in the business. Named approvers, business roles and
      approval emails all reach a person through this link, so a sign-in with
      no employee behind it is flagged rather than left to fail quietly.
    */
    { key: 'employee', header: 'Employee', width: 240,
      render: (row) => (
        row.employee ? (
          <>
            <div>{row.employee.designation ?? row.employee.name}</div>
            <div className="cell-sub">{row.employee.roleName ?? 'No business role'}</div>
            <Button kind="ghost" onClick={() => { setLinkFor(row); setLinkTo({ id: row.employee!.id, label: row.employee!.name }) }}>
              Change
            </Button>
          </>
        ) : (
          <>
            <div className="cell-sub">Not linked — cannot sign named steps or get approval emails</div>
            <Button kind="ghost" onClick={() => { setLinkFor(row); setLinkTo({ id: '', label: '' }) }}>Link to employee</Button>
          </>
        )
      ) },
    { key: 'lastLoginAt', header: 'Last signed in', width: 160,
      render: (row) => <span className="num">{row.lastLoginAt ? formatDate(row.lastLoginAt.slice(0, 10)) : 'never'}</span> },
    { key: 'active', header: 'Account', width: 200,
      render: (row) => (
        <>
          <div>{row.active ? 'Active' : 'Suspended'}</div>
          {row.mustChangePassword ? <div className="cell-sub">Temporary password</div> : null}
          {row.lockedUntil ? <div className="cell-sub">Locked after failed sign-ins</div> : null}
        </>
      ) },
    { key: 'actions', header: '', width: 330,
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 8 }}>
          <Button kind="ghost" onClick={() => setAccessFor(row)}>Access</Button>
          <Button kind="ghost" disabled={reset.isPending} onClick={() => reset.mutate(row)}>Reset password</Button>
          <Button
            kind="ghost"
            disabled={setActive.isPending}
            onClick={() => setActive.mutate({ id: row.id, active: !row.active })}
          >
            {row.active ? 'Suspend' : 'Restore'}
          </Button>
        </span>
      ) },
  ]

  return (
    <>
      <div style={{ display: 'flex', gap: 12, margin: '20px 0' }}>
        <Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={() => setAdding(true)}>
          Add someone
        </Button>
      </div>

      {error ? <div style={{ marginBottom: 16 }}><Notice kind="bad">{error}</Notice></div> : null}

      <Table columns={columns} rows={[...(users.data ?? [])]} getKey={(row) => row.id} />

      {adding ? (
        <Modal
          title="Add someone to this account"
          onClose={() => setAdding(false)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setAdding(false)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={add.isPending || draft.name.trim().length < 2 || !draft.email.includes('@')}
                onClick={() => add.mutate()}
              >
                Add them
              </Button>
            </>
          }
        >
          <Field label="Name" required>
            <Input value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          </Field>
          <Field label="Email address" required hint="They sign in with this.">
            <Input value={draft.email} onChange={(email) => setDraft({ ...draft, email })} />
          </Field>
          <Field label="Sign-in role" required hint="Administrators and owners can change settings and add people.">
            <Select
              value={draft.role}
              onChange={(role) => setDraft({ ...draft, role })}
              options={ROLE_OPTIONS}
            />
          </Field>
          <Field label="Business role" hint="What they may approve comes from this role's column in Roles and permissions.">
            <Select
              value={draft.roleId}
              onChange={(roleId) => setDraft({ ...draft, roleId })}
              options={roleOptions}
            />
          </Field>
          <Field label="Designation">
            <Input value={draft.designation} onChange={(designation) => setDraft({ ...draft, designation })} />
          </Field>
          <Field label="Mobile">
            <Input value={draft.mobile} onChange={(mobile) => setDraft({ ...draft, mobile })} />
          </Field>
          <Field label="Already an employee?" hint="Leave empty and an employee with this email is used, or a new one is made.">
            <Lookup
              entity="employee"
              value={draft.employeeId}
              label={draft.employeeLabel || undefined}
              placeholder="Search employees"
              onChange={(employeeId, option) => setDraft({ ...draft, employeeId, employeeLabel: option?.label ?? '' })}
            />
          </Field>
          <p className="muted">
            A temporary password is made for them and shown to you once. They choose their own when they first sign in.
          </p>
        </Modal>
      ) : null}

      {linkFor ? (
        <Modal
          title={`Which employee is ${linkFor.name}?`}
          onClose={() => setLinkFor(null)}
          actions={
            <>
              {linkFor.employee ? (
                <Button kind="ghost" disabled={link.isPending} onClick={() => link.mutate({ id: linkFor.id, employeeId: null })}>
                  Unlink
                </Button>
              ) : null}
              <Button kind="ghost" onClick={() => setLinkFor(null)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={link.isPending || linkTo.id === ''}
                onClick={() => link.mutate({ id: linkFor.id, employeeId: linkTo.id })}
              >
                Link
              </Button>
            </>
          }
        >
          <Field label="Employee" required>
            <Lookup
              entity="employee"
              value={linkTo.id}
              label={linkTo.label || undefined}
              placeholder="Search employees"
              onChange={(id, option) => setLinkTo({ id, label: option?.label ?? '' })}
            />
          </Field>
          <p className="muted">
            Their business role, named approval steps and approval emails follow this link. One employee signs in with
            one account.
          </p>
        </Modal>
      ) : null}

      {accessFor ? (
        <AccessDialog user={accessFor} onClose={() => setAccessFor(null)} />
      ) : null}

      {issued ? (
        <Modal
          title="Their temporary password"
          tone="warn"
          onClose={() => setIssued(null)}
          actions={<Button kind="primary" onClick={() => setIssued(null)}>I have taken a note of it</Button>}
        >
          <p>
            Give this to <strong>{issued.name}</strong>. It is shown once — nobody, including you, can read it
            back afterwards. They will be asked to choose their own when they sign in.
          </p>
          <p className="temp-password num">{issued.password}</p>
        </Modal>
      ) : null}
    </>
  )
}

/* ---- roles --------------------------------------------------------------- */

/** What each role may do beyond the documents, enforced in one place. */
const BEYOND_DOCUMENTS = [
  { what: 'Edit master data', owner: true, admin: true, member: true },
  { what: 'Change business settings', owner: true, admin: true, member: false },
  { what: 'Add people and change their roles', owner: true, admin: true, member: false },
  { what: 'Make someone else an owner', owner: true, admin: false, member: false },
]

/**
 * Cancelling and re-opening are the matrix's business now.
 *
 * They used to be gated on the sign-in role alone, which is why the table
 * above could not say anything useful about them.
 */
const BEYOND_NOTE =
  'Approving needs Approve on that document type; cancelling and re-opening need Full access. '
  + 'The sign-in role only decides who may administer the account — a Member whose business role '
  + 'approves purchase orders can approve them.'


/**
 * The five levels docs/15 R-10.1 specifies.
 *
 * `Full access` is the one that used to be missing, and its absence is why
 * cancelling and re-opening could not be granted or withheld per document
 * type: there was no level above Approve to hold them.
 */
const LEVEL_OPTIONS = [
  { value: 'NONE', label: 'No access' },
  { value: 'VIEW', label: 'See only' },
  { value: 'CREATE', label: 'Record and submit' },
  { value: 'APPROVE', label: 'Approve' },
  { value: 'FULL_ACCESS', label: 'Full access — including cancel and re-open' },
]

const ROLE_COLUMNS = [
  { role: 'OWNER', label: 'Owner' },
  { role: 'ADMIN', label: 'Administrator' },
  { role: 'MEMBER', label: 'Member' },
]

/**
 * The permission matrix (docs/15 §10.1): role × document type.
 *
 * A cell nobody has set shows the role's default, and says so — a matrix that
 * cannot tell a deliberate decision from an untouched default is a matrix
 * nobody trusts.
 */
const Roles = () => {
  const api = useApi()
  const client = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [showingLog, setShowingLog] = useState(false)

  const matrix = useQuery({ queryKey: ['adminPermissions', api.companyId], queryFn: () => api.adminPermissions() })

  const set = useMutation({
    mutationFn: ({ role, docType, level }: { role: string; docType: string; level: string }) =>
      api.adminSetPermission(role, docType, level),
    onSuccess: (next) => {
      client.setQueryData(['adminPermissions', api.companyId], next)
      void client.invalidateQueries({ queryKey: ['menu'] })
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (matrix.isLoading) return <Loading />
  if (matrix.error) return <ErrorBlock error={matrix.error} />

  const rows = matrix.data ?? []
  const types = [...new Map(rows.map((r) => [r.docType, r.docTypeLabel])).entries()]
  // The three sign-in roles, and then whatever roles the business named for
  // itself — the `app_role` master used to be a list that changed nothing.
  const columns = [...new Map(rows.map((r) => [r.role, r.roleLabel])).entries()]
    .map(([role, label]) => ({ role, label }))
  const levelOf = (role: string, docType: string): PermissionRow | undefined =>
    rows.find((r) => r.role === role && r.docType === docType)

  return (
    <>
      {error ? <div style={{ margin: '16px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      <Panel
        title="Documents"
        aside={
          <div className="row">
            <span className="muted">A cell left alone follows the role's default</span>
            {/* docs/11 §Permissions — `Changelog` sits on this screen, because
                this is the screen whose changes get asked about afterwards. */}
            <Button kind="ghost" onClick={() => setShowingLog(true)}>Changelog</Button>
          </div>
        }
      >
        <div className="matrix-scroll">
          <table className="matrix matrix--wide">
            <thead>
              <tr>
                <th scope="col">Document type</th>
                {columns.map((c) => <th scope="col" key={c.role}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {types.map(([docType, label]) => (
                <tr key={docType}>
                  <th scope="row">{label}</th>
                  {columns.map((column) => {
                    const cell = levelOf(column.role, docType)
                    return (
                      <td key={column.role}>
                        <Select
                          value={cell?.level ?? 'FULL_ACCESS'}
                          onChange={(level) => set.mutate({ role: column.role, docType, level })}
                          options={LEVEL_OPTIONS}
                          aria-label={`${label} for ${column.label}`}
                        />
                        {cell?.configured ? null : <div className="cell-sub">default</div>}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {showingLog ? (
        <PermissionLog onClose={() => setShowingLog(false)} />
      ) : null}

      <Panel title="Everything else">
        <table className="matrix">
          <thead>
            <tr>
              <th scope="col">Action</th>
              {ROLE_COLUMNS.map((c) => <th scope="col" key={c.role}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {BEYOND_DOCUMENTS.map((row) => (
              <tr key={row.what}>
                <th scope="row">{row.what}</th>
                {[row.owner, row.admin, row.member].map((allowed, i) => (
                  <td key={i} className={allowed ? 'matrix__yes' : 'matrix__no'}>{allowed ? 'Yes' : 'No'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted" style={{ marginTop: 16 }}>
          {BEYOND_NOTE}
        </p>
        <p className="muted" style={{ marginTop: 10 }}>
          An approval step can also name its own approvers, in which case only those people may sign that step —
          holding the permission is necessary, not sufficient. Whoever submitted a document never signs it
          themselves unless that is switched off in Settings.
        </p>
      </Panel>
    </>
  )
}

/* ---- numbering ----------------------------------------------------------- */

const Numbering = () => {
  const api = useApi()
  const client = useQueryClient()
  const [editing, setEditing] = useState<NumberSeriesView | null>(null)
  const [error, setError] = useState<string | null>(null)

  const series = useQuery({ queryKey: ['adminNumbering', api.companyId], queryFn: () => api.adminNumbering() })
  const tokens = useQuery({ queryKey: ['numberTokens'], queryFn: () => api.adminNumberTokens(), staleTime: 10 * 60_000 })

  const save = useMutation({
    mutationFn: (row: NumberSeriesView) => api.adminSaveNumbering(row),
    onSuccess: (next) => {
      client.setQueryData(['adminNumbering', api.companyId], next)
      setEditing(null)
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (series.isLoading) return <Loading />
  if (series.error) return <ErrorBlock error={series.error} />

  const columns: ColumnDef<NumberSeriesView>[] = [
    { key: 'docTypeLabel', header: 'Document type' },
    { key: 'prefix', header: 'Prefix', width: 160, render: (row) => <span className="num">{row.prefix}</span> },
    { key: 'nextValue', header: 'Next number', width: 140, align: 'right',
      render: (row) => <span className="num">{row.nextValue}</span> },
    { key: 'width', header: 'Digits', width: 100, align: 'right',
      render: (row) => <span className="num">{row.width}</span> },
    { key: 'financialYear', header: 'Year', width: 120 },
    { key: 'resetPeriod', header: 'Restarts', width: 130,
      render: (row) => (row.resetPeriod === 'NEVER' ? 'never' : row.resetPeriod === 'MONTHLY' ? 'each month' : 'each year') },
    { key: 'scope', header: 'Kept per', width: 130,
      render: (row) => (row.scope === 'JOB_TYPE' ? 'job type' : row.scope === 'TAX_UNIT' ? 'tax unit' : 'company') },
    { key: 'example', header: 'The next one will be', width: 220,
      render: (row) => <span className="num">{row.example}</span> },
    { key: 'actions', header: '', width: 110,
      render: (row) => <Button kind="ghost" onClick={() => setEditing(row)}>Change</Button> },
  ]

  return (
    <>
      {error ? <div style={{ margin: '16px 0' }}><Notice kind="bad">{error}</Notice></div> : null}
      <div style={{ marginTop: 20 }}>
        <Table columns={columns} rows={[...(series.data ?? [])]} getKey={(row) => row.id} />
      </div>

      {editing ? (
        <Modal
          title={`Numbering for ${editing.docTypeLabel.toLowerCase()}`}
          onClose={() => setEditing(null)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button kind="primary" disabled={save.isPending} onClick={() => save.mutate(editing)}>Save</Button>
            </>
          }
        >
          <Field label="Prefix" hint="What comes before the number. VEP/PO# gives VEP/PO#4728.">
            <Input value={editing.prefix} onChange={(prefix) => setEditing({ ...editing, prefix })} />
          </Field>
          <Field label="Next number" required hint="It can be moved forward, never back: a number already issued must not be given out twice.">
            <Input
              value={String(editing.nextValue)}
              onChange={(value) => setEditing({ ...editing, nextValue: Number(value.replace(/[^0-9]/g, '')) || 1 })}
            />
          </Field>
          <Field label="Digits" hint="4 pads 28 to 0028.">
            <Input
              value={String(editing.width)}
              onChange={(value) => setEditing({ ...editing, width: Number(value.replace(/[^0-9]/g, '')) || 1 })}
            />
          </Field>
          <Field
            label="Format"
            hint="Leave it empty for the prefix and the number. Otherwise build it from the tokens below."
          >
            <Input value={editing.format} onChange={(format) => setEditing({ ...editing, format })} />
          </Field>

          <div className="tokens">
            {(tokens.data ?? []).map((token) => (
              <button
                key={token.token}
                type="button"
                className="token"
                title={token.means}
                onClick={() => setEditing({ ...editing, format: `${editing.format}${token.token}` })}
              >
                {token.token}
              </button>
            ))}
          </div>

          <Field label="Restarts" hint="A counter that restarts yearly begins again on 1 April.">
            <Select
              value={editing.resetPeriod}
              onChange={(resetPeriod) => setEditing({ ...editing, resetPeriod })}
              options={[
                { value: 'YEARLY', label: 'Each financial year' },
                { value: 'MONTHLY', label: 'Each month' },
                { value: 'NEVER', label: 'Never — one continuous series' },
              ]}
            />
          </Field>

          <Field label="Kept separately per" hint="Each of these gets its own counter, so two GSTINs need not share a series.">
            <Select
              value={editing.scope}
              onChange={(scope) => setEditing({ ...editing, scope })}
              options={[
                { value: 'COMPANY', label: 'Company' },
                { value: 'JOB_TYPE', label: 'Job type' },
                { value: 'TAX_UNIT', label: 'Tax unit (GSTIN)' },
              ]}
            />
          </Field>

          <Field label="Financial year">
            <Input value={editing.financialYear} onChange={(financialYear) => setEditing({ ...editing, financialYear })} />
          </Field>
          <p className="muted">
            The next document of this type will be called{' '}
            <span className="num">
              {editing.format.trim() === ''
                ? `${editing.prefix}${String(editing.nextValue).padStart(editing.width, '0')}`
                : 'as the format above renders it — saved and shown in the list'}
            </span>.
          </p>
        </Modal>
      ) : null}
    </>
  )
}

/* ---- approval routes ------------------------------------------------------ */

interface WorkflowApprover { readonly id: string; readonly label: string }
interface WorkflowStep { readonly name: string; readonly approvers: readonly WorkflowApprover[] }

/**
 * One step of an approval route: what it is called, and who may sign it.
 *
 * Naming nobody means "anyone who may approve", which is what a route with no
 * named approvers has always meant. Naming somebody makes the step binding:
 * holding the permission stops being sufficient, which is exactly what the
 * permissions screen has been telling administrators all along.
 */
const StepEditor = ({ index, step, removable, onChange, onRemove }: {
  index: number
  step: WorkflowStep
  removable: boolean
  onChange: (next: WorkflowStep) => void
  onRemove: () => void
}) => (
  <div className="route-step">
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
      <div style={{ flex: 1 }}>
        <Field label={`Step ${index + 1}`}>
          <Input value={step.name} onChange={(name) => onChange({ ...step, name })} />
        </Field>
      </div>
      <Button kind="ghost" disabled={!removable} onClick={onRemove}>Remove</Button>
    </div>

    <Field
      label="Who signs it"
      hint={step.approvers.length === 0
        ? 'Leave this empty and anyone who may approve this document type can sign the step.'
        : 'Only these people may sign this step.'}
    >
      <Lookup
        entity="employee"
        value=""
        placeholder="Start typing a name to add an approver"
        onChange={(id, option) => {
          if (!id || step.approvers.some((a) => a.id === id)) return
          onChange({ ...step, approvers: [...step.approvers, { id, label: option?.label ?? id }] })
        }}
      />
    </Field>

    {step.approvers.length > 0 ? (
      <div className="row row--wrap" style={{ marginTop: 8 }}>
        {step.approvers.map((approver) => (
          <button
            key={approver.id}
            type="button"
            className="chip chip--removable"
            onClick={() => onChange({
              ...step,
              approvers: step.approvers.filter((a) => a.id !== approver.id),
            })}
          >
            {approver.label} <span aria-hidden="true">×</span>
            <span className="sr-only">Remove {approver.label}</span>
          </button>
        ))}
      </div>
    ) : null}
  </div>
)


const Workflows = () => {
  const api = useApi()
  const client = useQueryClient()
  /*
    A step is a name AND who signs it.

    The editor used to hold `steps: string[]`, so every step it wrote said
    'anyone who may approve' — while the list beside it rendered 'N named
    approver(s)' for routes that had them. The only way to name an approver was
    the API, and the server dropped the list again on the way to the document,
    so the feature was unreachable from the interface and inert behind it.
  */
  const [editing, setEditing] = useState<{
    docType: string
    label: string
    name: string
    steps: WorkflowStep[]
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const routes = useQuery({ queryKey: ['adminWorkflows', api.companyId], queryFn: () => api.adminWorkflows() })
  const types = useQuery({ queryKey: ['adminWorkflowTypes'], queryFn: () => api.adminWorkflowTypes(), staleTime: 10 * 60_000 })

  const save = useMutation({
    mutationFn: (asDraft: boolean) =>
      api.adminSaveWorkflow(
        editing!.docType,
        editing!.name,
        editing!.steps
          .filter((step) => step.name.trim() !== '')
          .map((step) => ({
            name: step.name,
            // An empty list means the step is not restricted, which the server
            // and the approval check both read as the sentinel.
            approvers: step.approvers.length > 0
              ? step.approvers.map((a) => a.id)
              : ('ANYONE_WITH_PERMISSION' as const),
          })),
        asDraft,
      ),
    onSuccess: (next) => {
      client.setQueryData(['adminWorkflows', api.companyId], next)
      setEditing(null)
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'LIVE' | 'DRAFT' | 'ARCHIVED' }) =>
      api.adminSetWorkflowStatus(id, status),
    onSuccess: (next) => {
      client.setQueryData(['adminWorkflows', api.companyId], next)
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  const live = useMemo(() => (routes.data ?? []).filter((r) => r.status === 'LIVE'), [routes.data])
  const drafts = useMemo(() => (routes.data ?? []).filter((r) => r.status === 'DRAFT'), [routes.data])
  const retired = useMemo(() => (routes.data ?? []).filter((r) => r.status === 'ARCHIVED'), [routes.data])

  if (routes.isLoading) return <Loading />
  if (routes.error) return <ErrorBlock error={routes.error} />

  const start = (route: WorkflowView | { key: string; label: string }): void => {
    if ('docType' in route) {
      setEditing({
        docType: route.docType,
        label: route.docTypeLabel,
        name: route.name,
        steps: route.stages.map((stage) => ({
          name: stage.name,
          approvers: stage.approvers === 'ANYONE_WITH_PERMISSION'
            ? []
            // The stored route holds ids; the names arrive as each is looked
            // up, and until then the id is what the chip shows.
            : stage.approvers.map((id) => ({ id, label: id })),
        })),
      })
    } else {
      setEditing({ docType: route.key, label: route.label, name: '', steps: [{ name: 'Approval', approvers: [] }] })
    }
  }

  const withoutRoute = (types.data ?? []).filter((t) => !live.some((r) => r.docType === t.key))

  return (
    <>
      {error ? <div style={{ margin: '16px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      <Panel title="In use" aside={`${live.length} route${live.length === 1 ? '' : 's'}`}>
        {live.length === 0 ? (
          <Empty title="No approval routes yet" body="Until a type has a route, its documents approve in a single step." />
        ) : (
          <div className="routes">
            {live.map((route) => (
              <div className="route" key={route.id}>
                <div className="route__head">
                  <div>
                    <div className="route__title">{route.docTypeLabel}</div>
                    <div className="route__meta">
                      {route.name} · version {route.version}
                      {route.inFlight > 0 ? ` · ${route.inFlight} document${route.inFlight === 1 ? '' : 's'} in review` : ''}
                    </div>
                  </div>
                  <div className="row">
                    <Button kind="ghost" onClick={() => start(route)}>Change</Button>
                    <Button
                      kind="ghost"
                      disabled={setStatus.isPending}
                      onClick={() => setStatus.mutate({ id: route.id, status: 'ARCHIVED' })}
                    >
                      Take out of service
                    </Button>
                  </div>
                </div>
                <ol className="route__steps">
                  {route.stages.map((stage) => (
                    <li key={stage.order}>
                      {stage.name}
                      <span className="route__who">
                        {stage.approvers === 'ANYONE_WITH_PERMISSION' ? 'anyone who may approve' : `${stage.approvers.length} named approver(s)`}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {withoutRoute.length > 0 ? (
        <Panel title="Without a route" aside={`${withoutRoute.length} type${withoutRoute.length === 1 ? '' : 's'}`}>
          <div className="line-sources">
            {withoutRoute.map((type) => (
              <Button key={type.key} kind="ghost" icon={<Icon name="plus" size={18} />} onClick={() => start(type)}>
                {type.label}
              </Button>
            ))}
          </div>
        </Panel>
      ) : null}

      {drafts.length > 0 ? (
        <Panel title="Drafts" aside="Written down, not yet in effect">
          <p className="helper">
            A draft changes nothing until it is published. Publishing it takes the route currently
            in service out of service — documents already in review keep the one they entered.
          </p>
          <div className="routes">
            {drafts.map((route) => (
              <div className="route" key={route.id}>
                <div className="route__head">
                  <div>
                    <div className="route__title">{route.docTypeLabel}</div>
                    <div className="route__meta">{route.name} · version {route.version} · draft</div>
                  </div>
                  <div className="row">
                    <Button
                      kind="primary"
                      disabled={setStatus.isPending}
                      onClick={() => setStatus.mutate({ id: route.id, status: 'LIVE' })}
                    >
                      Put it into service
                    </Button>
                    <Button
                      kind="ghost"
                      disabled={setStatus.isPending}
                      onClick={() => setStatus.mutate({ id: route.id, status: 'ARCHIVED' })}
                    >
                      Discard it
                    </Button>
                  </div>
                </div>
                <ol className="route__steps">
                  {route.stages.map((stage) => <li key={stage.order}>{stage.name}</li>)}
                </ol>
              </div>
            ))}
          </div>
        </Panel>
      ) : null}

      {retired.length > 0 ? (
        <Panel title="Previous versions" aside="Kept, because documents approved under them refer to them">
          <ul className="route__history">
            {retired.map((route) => (
              <li key={route.id}>
                {route.docTypeLabel} — version {route.version}, {route.stages.length} step
                {route.stages.length === 1 ? '' : 's'}
                {' '}
                <button
                  type="button"
                  className="link-button"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate({ id: route.id, status: 'LIVE' })}
                >
                  Put this one back into service
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      {editing ? (
        <Modal
          title={`Approval route for ${editing.label.toLowerCase()}`}
          onClose={() => setEditing(null)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                kind="ghost"
                disabled={save.isPending || editing.steps.every((step) => step.name.trim() === '')}
                onClick={() => save.mutate(true)}
              >
                Save as a draft
              </Button>
              <Button
                kind="primary"
                disabled={save.isPending || editing.steps.every((step) => step.name.trim() === '')}
                onClick={() => save.mutate(false)}
              >
                Publish this route
              </Button>
            </>
          }
        >
          <Field label="What to call this route" hint="For your own reference, on the audit trail.">
            <Input value={editing.name} onChange={(name) => setEditing({ ...editing, name })} />
          </Field>

          <p style={{ margin: '18px 0 10px' }}>
            <strong>The steps, in order.</strong> The last one approves the document; the others pass it along.
          </p>

          {editing.steps.map((step, index) => (
            <StepEditor
              key={index}
              index={index}
              step={step}
              removable={editing.steps.length > 1}
              onChange={(next) => {
                const steps = [...editing.steps]
                steps[index] = next
                setEditing({ ...editing, steps })
              }}
              onRemove={() => setEditing({ ...editing, steps: editing.steps.filter((_, i) => i !== index) })}
            />
          ))}

          <Button
            kind="ghost"
            icon={<Icon name="plus" size={18} />}
            disabled={editing.steps.length >= 8}
            onClick={() => setEditing({ ...editing, steps: [...editing.steps, { name: '', approvers: [] }] })}
          >
            Add a step
          </Button>

          <Notice kind="info">
            Publishing makes a new version. Documents already in review keep the route they entered, so nothing
            in flight changes under anyone.
          </Notice>
        </Modal>
      ) : null}
    </>
  )
}

/* ---- companies ------------------------------------------------------------ */

const blankCompany: CompanyView = {
  id: '', name: '', legalName: '', abbreviation: '', gstin: null, panNo: null, email: null, active: true,
}

const Companies = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const client = useQueryClient()
  const [editing, setEditing] = useState<CompanyView | null>(null)
  const [error, setError] = useState<string | null>(null)

  const companies = useQuery({ queryKey: ['adminCompanies', api.companyId], queryFn: () => api.adminCompanies() })

  const save = useMutation({
    mutationFn: (company: CompanyView) => api.adminSaveCompany(company),
    onSuccess: (next) => {
      client.setQueryData(['adminCompanies', api.companyId], next)
      void client.invalidateQueries({ queryKey: ['session'] })
      setEditing(null)
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (companies.isLoading) return <Loading />
  if (companies.error) return <ErrorBlock error={companies.error} />

  const columns: ColumnDef<CompanyView>[] = [
    { key: 'name', header: 'Company',
      render: (row) => (
        <>
          <div>{row.name}{row.id === companyId ? ' — you are in this one' : ''}</div>
          <div className="cell-sub">{row.legalName}</div>
        </>
      ) },
    { key: 'abbreviation', header: 'Short name', width: 140, render: (row) => <span className="num">{row.abbreviation}</span> },
    { key: 'gstin', header: 'GSTIN', width: 200, render: (row) => <span className="num">{row.gstin ?? '—'}</span> },
    { key: 'active', header: 'Status', width: 120, render: (row) => (row.active ? 'Active' : 'Closed') },
    { key: 'actions', header: '', width: 110,
      render: (row) => <Button kind="ghost" onClick={() => setEditing(row)}>Change</Button> },
  ]

  return (
    <>
      <div style={{ display: 'flex', gap: 12, margin: '20px 0' }}>
        <Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={() => setEditing(blankCompany)}>
          Add a company
        </Button>
      </div>

      {error ? <div style={{ marginBottom: 16 }}><Notice kind="bad">{error}</Notice></div> : null}

      <Table columns={columns} rows={[...(companies.data ?? [])]} getKey={(row) => row.id || 'new'} />

      {editing ? (
        <Modal
          title={editing.id ? editing.name : 'Add a company'}
          onClose={() => setEditing(null)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={save.isPending || editing.name.trim().length < 2 || editing.abbreviation.trim().length < 2}
                onClick={() => save.mutate(editing)}
              >
                Save
              </Button>
            </>
          }
        >
          <Field label="Name" required hint="What people here call it.">
            <Input value={editing.name} onChange={(name) => setEditing({ ...editing, name })} />
          </Field>
          <Field label="Legal name" hint="As registered. Printed on invoices.">
            <Input value={editing.legalName} onChange={(legalName) => setEditing({ ...editing, legalName })} />
          </Field>
          <Field label="Short name" required hint="Two to ten letters or digits, used in document numbers.">
            <Input value={editing.abbreviation} onChange={(abbreviation) => setEditing({ ...editing, abbreviation })} />
          </Field>
          <Field label="GSTIN">
            <Input value={editing.gstin ?? ''} onChange={(gstin) => setEditing({ ...editing, gstin })} />
          </Field>
          <Field label="PAN">
            <Input value={editing.panNo ?? ''} onChange={(panNo) => setEditing({ ...editing, panNo })} />
          </Field>
          <Field label="Email">
            <Input value={editing.email ?? ''} onChange={(email) => setEditing({ ...editing, email })} />
          </Field>
        </Modal>
      ) : null}
    </>
  )
}

/** docs/15 R-10.2 — the three values every row scope takes. */
const ROW_SCOPES = [
  { value: 'ALL', label: 'All records' },
  { value: 'ASSIGNED', label: 'Only their assigned projects' },
  { value: 'OWN', label: 'Only what they raised themselves' },
]

/**
 * What one person may reach: their companies, their projects, whose records
 * they may see, and whether the account is support access that expires
 * (docs/15 §10.2, §10.3, §10.4, §10.7).
 */
const AccessDialog = ({ user, onClose }: { user: UserView; onClose: () => void }) => {
  const api = useApi()
  const client = useQueryClient()
  const [draft, setDraft] = useState<{
    userType: string; projectScope: string; companyIds: string[]; projectIds: string[]; delegateDays: string
    toolsScope: string; stockPurchaseScope: string
    reportDownloadScope: string; expenseReportScope: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const access = useQuery({
    queryKey: ['userAccess', api.companyId, user.id],
    queryFn: () => api.adminUserAccess(user.id),
  })

  // Seeded once the answer arrives; editing before that would edit nothing.
  const current = access.data
  const state = draft ?? (current
    ? {
        userType: current.userType,
        projectScope: current.projectScope,
        companyIds: [...current.companyIds],
        projectIds: [...current.projectIds],
        delegateDays: '7',
        toolsScope: current.toolsScope,
        stockPurchaseScope: current.stockPurchaseScope,
        reportDownloadScope: current.reportDownloadScope,
        expenseReportScope: current.expenseReportScope,
      }
    : null)

  const save = useMutation({
    mutationFn: () => api.adminSetUserAccess(user.id, {
      userType: state!.userType,
      projectScope: state!.projectScope,
      companyIds: state!.companyIds,
      projectIds: state!.projectIds,
      toolsScope: state!.toolsScope,
      stockPurchaseScope: state!.stockPurchaseScope,
      reportDownloadScope: state!.reportDownloadScope,
      expenseReportScope: state!.expenseReportScope,
      ...(state!.userType === 'DELEGATE' ? { delegateDays: Number(state!.delegateDays) || 7 } : {}),
    }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['adminUsers', api.companyId] })
      onClose()
    },
    onError: (err) => setError(messageFor(err)),
  })

  // docs/15 R-10.7 — support access ends when the work is done, not when the
  // clock runs out.
  const revoke = useMutation({
    mutationFn: () => api.adminRevokeDelegate(user.id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['userAccess', api.companyId, user.id] })
      void client.invalidateQueries({ queryKey: ['adminUsers', api.companyId] })
      onClose()
    },
    onError: (err) => setError(messageFor(err)),
  })

  const toggle = (list: string[], id: string): string[] =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id]

  return (
    <Modal
      title={`What ${user.name} may reach`}
      wide
      onClose={onClose}
      actions={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button kind="primary" disabled={!state || save.isPending} onClick={() => save.mutate()}>Save</Button>
        </>
      }
    >
      {access.isLoading || !state ? <Loading /> : (
        <>
          {error ? <div style={{ marginBottom: 14 }}><Notice kind="bad">{error}</Notice></div> : null}

          <Field label="Kind of account" hint="Support access is temporary and stops working on its own.">
            <Select
              value={state.userType}
              onChange={(userType) => setDraft({ ...state, userType })}
              options={[
                { value: 'NON_ADMIN', label: 'Ordinary account' },
                { value: 'ADMIN', label: 'Administrator account' },
                { value: 'DELEGATE', label: 'Support access, temporary' },
              ]}
            />
          </Field>

          {state.userType === 'DELEGATE' ? (
            <>
              <Field label="For how many days?" hint="At most ninety. It stops working then, with nothing to remember.">
                <Input value={state.delegateDays} onChange={(delegateDays) => setDraft({ ...state, delegateDays })} />
              </Field>
              {current?.delegateExpiresAt ? (
                <div className="row" style={{ marginBottom: 18 }}>
                  <span className="helper">
                    Access runs out on {formatDate(current.delegateExpiresAt)}.
                  </span>
                  <Button kind="ghost" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
                    End it now
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}

          <Field label="Which projects" hint="Assigned scope hides every document of every other project — in lists, reports and type-aheads alike.">
            <Select
              value={state.projectScope}
              onChange={(projectScope) => setDraft({ ...state, projectScope })}
              options={[
                { value: 'ALL', label: 'Every project in the company' },
                { value: 'ASSIGNED', label: 'Only the projects ticked below' },
              ]}
            />
          </Field>

          {state.projectScope === 'ASSIGNED' ? (
            <div className="column-choices" style={{ marginBottom: 18 }}>
              {(current?.projects ?? []).map((project) => (
                <label className="column-choice" key={project.id}>
                  <input
                    type="checkbox"
                    checked={state.projectIds.includes(project.id)}
                    onChange={() => setDraft({ ...state, projectIds: toggle(state.projectIds, project.id) })}
                  />
                  <span>{project.name}</span>
                </label>
              ))}
            </div>
          ) : null}

          {/*
            docs/15 R-10.2 — four more scopes, separate from the permission
            matrix. The matrix says what someone may DO with a kind of document;
            these say whose records they may do it to.
          */}
          <h3 className="subhead" style={{ marginTop: 20 }}>Whose records</h3>
          <div className="form-grid">
            <Field label="Tools" hint="Tool entries, requests and issues.">
              <Select
                value={state.toolsScope}
                onChange={(toolsScope) => setDraft({ ...state, toolsScope })}
                options={ROW_SCOPES}
              />
            </Field>
            <Field label="Stock purchase requests">
              <Select
                value={state.stockPurchaseScope}
                onChange={(stockPurchaseScope) => setDraft({ ...state, stockPurchaseScope })}
                options={ROW_SCOPES}
              />
            </Field>
            <Field label="Expense reports" hint="Somebody's own claims and advances.">
              <Select
                value={state.expenseReportScope}
                onChange={(expenseReportScope) => setDraft({ ...state, expenseReportScope })}
                options={ROW_SCOPES}
              />
            </Field>
            <Field
              label="Report download"
              hint="Own records means reports can be read on screen but not downloaded."
            >
              <Select
                value={state.reportDownloadScope}
                onChange={(reportDownloadScope) => setDraft({ ...state, reportDownloadScope })}
                options={ROW_SCOPES}
              />
            </Field>
          </div>

          <p style={{ margin: '18px 0 8px' }}>
            <strong>Which companies.</strong> Tick none to allow every company on this account.
          </p>
          <div className="column-choices">
            {(current?.companies ?? []).map((company) => (
              <label className="column-choice" key={company.id}>
                <input
                  type="checkbox"
                  checked={state.companyIds.includes(company.id)}
                  onChange={() => setDraft({ ...state, companyIds: toggle(state.companyIds, company.id) })}
                />
                <span>{company.name}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </Modal>
  )
}

/** Every sign-in attempt (docs/15 §10.8). Failures are the point. */
const OUTCOMES: Readonly<Record<string, string>> = {
  SUCCESS: 'Signed in',
  BAD_PASSWORD: 'Wrong password',
  UNKNOWN_EMAIL: 'No such account',
  LOCKED: 'Account locked',
  SUSPENDED: 'Account suspended',
  IP_REFUSED: 'Network not allowed',
  DELEGATE_EXPIRED: 'Support access expired',
}

const LoginRegister = () => {
  const api = useApi()
  const register = useQuery({ queryKey: ['loginRegister', api.companyId], queryFn: () => api.adminLoginRegister() })

  if (register.isLoading) return <Loading />
  if (register.error) return <ErrorBlock error={register.error} />

  const rows = [...(register.data ?? [])]
  const failures = rows.filter((r) => r.outcome !== 'SUCCESS').length

  const columns: ColumnDef<LoginEventView>[] = [
    { key: 'at', header: 'When', width: 200,
      render: (row) => <span className="num">{new Date(row.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span> },
    { key: 'email', header: 'Email address' },
    { key: 'outcome', header: 'What happened', width: 200,
      render: (row) => (
        <span style={row.outcome === 'SUCCESS' ? undefined : { color: 'var(--bad)', fontWeight: 600 }}>
          {OUTCOMES[row.outcome] ?? row.outcome}
        </span>
      ) },
    { key: 'ip', header: 'From', width: 170, render: (row) => <span className="num">{row.ip ?? '—'}</span> },
    { key: 'browser', header: 'Browser', width: 130 },
    { key: 'os', header: 'System', width: 130 },
  ]

  return (
    <Panel
      title="Sign-in attempts"
      aside={failures > 0 ? `${failures} of the last ${rows.length} failed` : `${rows.length} attempts, none failed`}
    >
      {rows.length === 0 ? (
        <Empty title="Nothing recorded yet" body="Every attempt to sign in appears here, successful or not." />
      ) : (
        <Table columns={columns} rows={rows} getKey={(row) => row.id} />
      )}
    </Panel>
  )
}

/* ---- changing your own password ------------------------------------------- */

export const ChangePasswordPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const change = useMutation({
    mutationFn: () => api.changePassword(current, next),
    onSuccess: () => {
      setDone(true)
      setError(null)
      setCurrent('')
      setNext('')
      setAgain('')
    },
    onError: (err) => setError(messageFor(err)),
  })

  const mismatch = again !== '' && next !== again

  return (
    <div className="page page--narrow">
      <h1 className="page-title">Your password</h1>
      <p className="page-subtitle">Changing it signs out every other device you are signed in on.</p>

      {done ? <div style={{ margin: '18px 0' }}><Notice kind="ok">Your password has been changed.</Notice></div> : null}
      {error ? <div style={{ margin: '18px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      <Panel title="Change it">
        <Field label="Your current password" required>
          <Input type="password" value={current} onChange={setCurrent} />
        </Field>
        <Field label="New password" required hint="At least eight characters, with an uppercase letter, a number and a special character.">
          <Input type="password" value={next} onChange={setNext} />
        </Field>
        <Field label="New password again" required error={mismatch ? 'These two do not match.' : undefined}>
          <Input type="password" value={again} onChange={setAgain} />
        </Field>
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button
            kind="primary"
            disabled={change.isPending || current === '' || next === '' || mismatch}
            onClick={() => change.mutate()}
          >
            Change my password
          </Button>
          <Button kind="ghost" onClick={() => navigate(paths.home(companyId))}>Back</Button>
        </div>
      </Panel>
    </div>
  )
}

/* ============================== changelog ================================= */

/**
 * `Changelog` — docs/11 §Permissions, docs/15 R-10.1, docs/16 AC-1.6.
 *
 * Every one of these entries was already being written and none of them could
 * be read. The question this answers — "who gave them approval rights, and
 * when?" — gets asked after something has gone wrong, by somebody who has no
 * way to reach the database.
 *
 * Nothing here can be edited: the application role holds no UPDATE or DELETE
 * on the audit table, so the screen is telling the truth about what it is.
 */
const Changelog = () => {
  const api = useApi()
  const [entity, setEntity] = useState('')
  const entries = useQuery({
    queryKey: ['changelog', api.companyId, entity],
    queryFn: () => api.adminChangelog(entity === '' ? {} : { entity }),
  })

  if (entries.isLoading) return <Loading />
  if (entries.error) return <ErrorBlock error={entries.error} />

  const rows = entries.data ?? []
  // The filter offers what is actually in the log, rather than a fixed list
  // that goes stale the moment an entity stops being audited.
  const kinds = [...new Map(rows.map((r) => [r.entity, r.entityLabel])).entries()]

  const columns: ReadonlyArray<ColumnDef<ChangelogEntry>> = [
    { key: 'at', header: 'When', width: 190, render: (r) => formatDateTime(r.at) },
    { key: 'actorName', header: 'Who', width: 200 },
    { key: 'entityLabel', header: 'What', width: 180 },
    { key: 'actionLabel', header: 'Change', width: 190 },
    {
      key: 'detail',
      header: 'Detail',
      render: (r) => <ChangeDetail entry={r} />,
    },
  ]

  return (
    <Panel
      title="What has been changed"
      aside={
        <div style={{ minWidth: 220 }}>
          <Select
            value={entity}
            onChange={setEntity}
            placeholder="Everything"
            options={kinds.map(([value, label]) => ({ value, label }))}
          />
        </div>
      }
    >
      <p className="helper">
        The most recent 200 changes. This cannot be edited or removed by anyone, including an
        owner — the application has no permission to write over it.
      </p>
      <Table
        columns={columns}
        rows={rows}
        getKey={(row) => row.id}
        empty={<Empty title="Nothing has been changed yet" body={['Administrative changes appear here as they are made.']} />}
      />
    </Panel>
  )
}

/** What actually changed, in the fewest words that still say it. */
const ChangeDetail = ({ entry }: { entry: ChangelogEntry }) => {
  const before = entry.before ?? {}
  const after = entry.after ?? {}
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  if (keys.length === 0) return <span className="muted">—</span>

  const write = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  return (
    <>
      {keys.slice(0, 4).map((key) => (
        <div key={key} className="cell-sub">
          <strong>{key}</strong>{' '}
          {key in before && write(before[key]) !== write(after[key])
            ? <>{write(before[key])} → {write(after[key])}</>
            : write(after[key] ?? before[key])}
        </div>
      ))}
      {keys.length > 4 ? <div className="cell-sub muted">…and {keys.length - 4} more</div> : null}
    </>
  )
}

/** The permission changes alone, from the screen that made them. */
const PermissionLog = ({ onClose }: { onClose: () => void }) => {
  const api = useApi()
  const entries = useQuery({
    queryKey: ['changelog', api.companyId, 'role_permission'],
    queryFn: () => api.adminChangelog({ entity: 'role_permission' }),
  })

  const columns: ReadonlyArray<ColumnDef<ChangelogEntry>> = [
    { key: 'at', header: 'When', width: 180, render: (r) => formatDateTime(r.at) },
    { key: 'actorName', header: 'Who', width: 180 },
    { key: 'detail', header: 'What changed', render: (r) => <ChangeDetail entry={r} /> },
  ]

  return (
    <Modal
      title="Changes to permissions"
      subtitle="Who changed what a role may do, and when."
      wide
      actions={<Button kind="primary" onClick={onClose}>Close</Button>}
      onClose={onClose}
    >
      {entries.isLoading ? <Loading /> : null}
      {entries.error ? <ErrorBlock error={entries.error} /> : null}
      <Table
        columns={columns}
        rows={entries.data ?? []}
        getKey={(row) => row.id}
        empty={<Empty title="No permission has been changed yet" body={['Everything is still on its default.']} />}
      />
    </Modal>
  )
}
